import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { secureStorage } from "../lib/secureStorage";
import { BACKEND_URL } from "../lib/backendUrl";

const AuthContext = React.createContext();

const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes

export const AuthProvider = ({ children }) => {
  // Init synchrone : évite la redirection /login au 1er rendu lors d'un chargement direct (ex. /borne)
  const [user, setUser] = useState(() => (secureStorage.getToken() ? secureStorage.getUser() || null : null));
  const [token, setToken] = useState(secureStorage.getToken());
  const idleTimer = useRef(null);

  useEffect(() => {
    if (token) {
      const userData = secureStorage.getUser();
      if (userData) {
        setUser(userData);
      }
    }
  }, [token]);

  // Déconnexion automatique après 30 minutes d'inactivité
  useEffect(() => {
    if (!token) return undefined;
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    const reset = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        toast.warning('Session expirée après 30 minutes d\'inactivité. Veuillez vous reconnecter.');
        logout();
      }, INACTIVITY_LIMIT_MS);
    };
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [token]);

  const login = (userData, authToken, refreshToken) => {
    setUser(userData);
    setToken(authToken);
    secureStorage.setToken(authToken);
    secureStorage.setUser(userData);
    if (refreshToken) secureStorage.setRefreshToken(refreshToken);
  };

  const logout = () => {
    // Révocation best-effort du jeton de rafraîchissement côté serveur (ne bloque pas la déconnexion)
    const rt = secureStorage.getRefreshToken();
    if (rt && navigator.onLine) {
      try {
        fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: rt }),
          keepalive: true,
        }).catch(() => {});
      } catch (e) {
        console.warn('Logout revoke best-effort échoué:', e);
      }
    }
    setUser(null);
    setToken(null);
    secureStorage.clearAuth();
  };

  const updateUser = (nextUser) => {
    setUser(nextUser);
    secureStorage.setUser(nextUser);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
